import { FlamingoAdapter } from '@mode-switch/adapter-flamingo';
import { RecordsPlan, RecordsQuery } from '@mode-switch/query';
import {
  AccountModelAccessor,
  ReportModelAccessor,
  ReportRunModelAccessor,
  ReportRunResource,
} from '@mode/legacy/data-access-model-js';
import { ReportFilterFacade, ReportViewerActions, ReportViewerFacade } from '@mode/report-viewer/domain';
import { FINISHED_STATES, FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import {
  dimensions,
  makeQueryPlanMetadataId,
  makeQueryWithCalcsPlanMetadataId,
  MetaTags,
  getCurrentUsername,
  getInitialFlagValue,
  getOwnerUsername,
  getReportOwnerUsername,
  getReportRunToken,
  setMetaContent,
} from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { recordEvent } from '@mode/shared/util-tracing';
import { FlamingoActions } from '@mode/switch-views/contract';
import { DataViewFacade, PlanMetadataFacade } from '@mode/switch-views/contract';
import type * as ngRouter from '@uirouter/angularjs';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, combineLatest, from } from 'rxjs';
import { filter, first, map, shareReplay, skip, switchMap, withLatestFrom } from 'rxjs/operators';
import { AppStore } from '~/types/main.types';
import { ReportActionType } from '../../../actions/report-actions';
import { ReportRunActionType } from '../../../actions/report-run.actions';
import {
  routerSelector,
  selectedReportLastSuccessfulRunTokenSelector,
  selectedReportResourceSelector,
} from '../../../reducers/selectors';
import { ReportLayoutSelectors } from '../../../selectors/report-layout.selectors';
import { SelectedReportSelectors } from '../../../selectors/selected-report.selectors';
import { SqlResultsSelectors } from '../../../selectors/sql-results.selectors';
import { ClientDataService } from '../../../services/client-data-service';
import DatasetService from '../../../services/dataset-service';
import { FullscreenService } from '../../../services/fullscreen-service';
import { NotebookDataService } from '../../../services/notebook-data-service';
import { ReportDataService } from '../../../services/report-data-service';
import { ReportRunDataService } from '../../../services/report-run-service';
import { ReportStateService } from '../../../services/report-state-service';
import { WindowMessageService } from '../../../services/window-message-service';

const adapter = new FlamingoAdapter();

export const REPORT_VIEW_DATA_SERVICE_ID = 'ReportViewDataService';

export const QUERY_RESOURCE_PROPERTIES = [
  'created_at',
  'data_source_id',
  'explorations_count',
  'id',
  'last_run_id',
  'mapping_id',
  'name',
  'raw_query',
  'report_imports_count',
  'token',
  'updated_at',
];

export class ReportViewDataService {
  static $inject = [
    'ClientDataService',
    'FullscreenService',
    'WindowMessageService',
    'ReportStateService',
    'RxStoreService',
    'DatasetService',
    'DataViewFacade',
    'FeatureFlagsFacade',
    'NotebookDataService',
    'ReportDataService',
    'ReportRunDataService',
    'ReportViewerFacade',
    'ReportFilterFacade',
    'ObservabilityService',
    'AccountModel',
    'ReportRunModel',
    'ReportModel',
    'ModeCollection',
    'PreferenceModel',
    '$state',
    '$window',
    'PlanMetadataFacade',
  ];

  reportIsShared: boolean;
  reportIsEmbedded: boolean;
  reportIsWhiteLabelEmbedded: boolean;
  preference: any;
  alert: any;
  runId: any;
  accountId: any;
  account: any;
  account$ = new BehaviorSubject(null);
  contextAccountId: any;
  reportOwnerId: any;
  reportOwner: any;
  contextAccount$ = new BehaviorSubject(null);
  errorNoToken: boolean;
  spaces: any;
  run: any;
  reportRun$ = new BehaviorSubject(null);
  space: any;
  schedulesCollection$ = new BehaviorSubject(null);
  savedNotebook: any;
  exportableNotebook: boolean;
  report: any;
  report$ = new BehaviorSubject(null);

  exportableDatasets: boolean;
  executing: any;
  allowableOrgs: any;
  reportRunsCollection: any;
  reportRunsCollection$ = new BehaviorSubject(null);

  isReportViewDetailsTab$: Observable<any>;
  hasScriptOrModeTags$: Observable<boolean>;
  runToken$: Observable<string>;
  hasRun$: Observable<boolean>;
  private isExportDatasetsEnabled = getInitialFlagValue(FeatureFlag.NotebookExportDatasets);

  constructor(
    private clientDataService: ClientDataService,
    private fullscreenService: FullscreenService,
    private windowMessageService: WindowMessageService,
    private reportStateService: ReportStateService,
    private store: AppStore,
    private datasetService: DatasetService,
    private dataViewFacade: DataViewFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private notebookDataService: NotebookDataService,
    private reportDataService: ReportDataService,
    private reportRunDataService: ReportRunDataService,
    private reportViewerFacade: ReportViewerFacade,
    private filterFacade: ReportFilterFacade,
    private observabilityService: ObservabilityService,
    private accountModel: AccountModelAccessor,
    private reportRunModel: ReportRunModelAccessor,
    private reportModel: ReportModelAccessor,
    private modeCollection: any,
    private preferenceModel,
    private $state: ngRouter.StateService,
    private $window: angular.IWindowService,
    private planMetadataFacade: PlanMetadataFacade
  ) {
    const reportResource$ = this.store.select(selectedReportResourceSelector);

    const lastSuccessfulRunToken$ = this.store.selectNoEmpty(selectedReportLastSuccessfulRunTokenSelector);
    const currentRunState$ = this.store.selectNoEmpty(SelectedReportSelectors.getReportRunState);
    const currentRunToken$ = this.store.selectNoEmpty(SelectedReportSelectors.getReportRunToken);

    combineLatest([lastSuccessfulRunToken$, currentRunToken$, currentRunState$])
      .pipe(
        map(
          ([lastSuccessfulRunToken, reportRunToken, currentRunState]) =>
            // To clarify, 'enqueued' means the run is still running. The current run has this state on page load
            // when you click 'Run' from the report view page. For this reason, we can only compare the tokens if
            // the current run state is not 'enqueued'. If it is 'enqueued', we can assume it is the most recent run.
            FINISHED_STATES.includes(currentRunState) && reportRunToken !== lastSuccessfulRunToken
        )
      )
      .subscribe((hasRecentReportRun) => {
        this.store.dispatch({
          type: ReportActionType.UpdateRecentReport,
          payload: {
            hasRecentReportRun,
          },
        });
      });

    // Side-effect if we have recent run and we need recent run details
    combineLatest([this.store.select(SelectedReportSelectors.hasRecentReportRun), reportResource$])
      .pipe(
        filter(([hasNewerRun]) => hasNewerRun),
        switchMap(([, report]) => {
          const lastReportRun = from(report.links('last_run').$follow()) as Observable<ReportRunResource>;
          return lastReportRun;
        }),
        map((lastReportRun) => {
          const { state: lastRunState } = lastReportRun;
          const lastReportRunLink = lastReportRun.links('share').href;
          const lastReportRunId = lastReportRun.token;

          return {
            lastRunState,
            lastReportRunLink,
            lastReportRunId,
          };
        })
      )
      .subscribe((recentReportRun) => {
        this.store.dispatch({
          type: ReportActionType.UpdateRecentReportDetails,
          payload: {
            recentReportRun,
          },
        });
      });

    // handle query-view side-effects
    const datasetViewStateNames = [
      'reportShow.noRuns.queries.data',
      'reportShow.runs.queries.data',
      'reportShow.noRuns.detailsView.detailsDataset',
      'reportShow.runs.detailsView.detailsDataset',
    ];
    this.store
      .select(routerSelector)
      .pipe(
        filter(
          (routerState) =>
            datasetViewStateNames.includes(routerState.name) && routerState.params.hasOwnProperty('queryId')
        ),
        map(({ params: { queryId } }) => queryId),
        switchMap((queryToken) =>
          this.dataViewFacade.createRunReadyByQueryToken(queryToken).pipe(
            filter((ready) => ready),
            switchMap((_) =>
              this.store.selectNoEmpty(SelectedReportSelectors.createQueryRunTokenSelector(queryToken)).pipe(first())
            ),
            withLatestFrom(
              this.store.select(SelectedReportSelectors.getColumnModel),
              this.store.select(SqlResultsSelectors.getSelectedQueryOffset)
            ),
            map(([runToken, model, offset]) => ({
              runToken,
              queryToken,
              plan: RecordsPlan.make(
                adapter,
                model,
                queryToken,
                RecordsQuery.make({
                  limit: 100,
                  offset: offset,
                  fields: dimensions(adapter, model, queryToken),
                })
              ),
            }))
          )
        )
      )
      .subscribe((result) => {
        this.planMetadataFacade.initPaging(makeQueryPlanMetadataId(result.queryToken));
        this.planMetadataFacade.initPaging(makeQueryWithCalcsPlanMetadataId(result.queryToken));
        // The first one is really just to init pagination in sqlResult state tree.
        this.store.dispatch({ type: ReportActionType.InitQueryResultPagination, token: result.queryToken });
        this.store.dispatch(
          FlamingoActions.executePlan({
            plan: result.plan,
            refId: result.queryToken,
            queryRunToken: result.runToken,
            allowRefresh: true,
          })
        );
      });
  }

  lastSuccessfulRunToken$ = this.store.selectNoEmpty(selectedReportLastSuccessfulRunTokenSelector).pipe(shareReplay(1));

  /**
   * Do setup required for any type of initialization.
   */
  public setup() {
    this.runId = getReportRunToken(this.$window.document);
    this.accountId = getCurrentUsername(this.$window.document);
    this.reportOwnerId = getReportOwnerUsername(this.$window.document);
    this.contextAccountId = getOwnerUsername(this.$window.document);
    this.clientDataService.setLoggedInState(!!this.accountId);
  }

  /**
   * Initialize data necessary to render a container (regardless of content)
   * - This is currently used in the exploration context to initialize Accounts
   *   that are necessary for the header and flyout nav to render
   * TODO: This will probably be moved and refactored once it's clear what a better
   * pattern looks like for this.
   */
  public initContainer() {
    this.setup();
    this.initializeAccounts();
  }

  /**
   * Initialize a report, specifically.
   * - This contains the current flow for report initialization completely unchanged.
   */
  public initReport() {
    this.setup();

    this.isReportViewDetailsTab$ = this.store
      .select(routerSelector)
      .pipe(
        map(
          (routerState) =>
            routerState.name === 'reportShow.noRuns.dashboard' || routerState.name === 'reportShow.runs.dashboard'
        )
      );

    this.hasScriptOrModeTags$ = combineLatest(
      this.store.select(ReportLayoutSelectors.hasScriptTag),
      this.store.select(ReportLayoutSelectors.hasVizElementsInLayout)
    ).pipe(map(([scriptFlag, vizFlag]) => !(scriptFlag || vizFlag)));

    this.fullscreenService.isFullscreen$.pipe(skip(1)).subscribe((fullscreen) => {
      if (fullscreen) {
        this.handleEnterFullScreen();
      } else {
        this.handleExitFullScreen();
      }
    });

    this.reportDataService.selectedReportResourceState$
      .pipe(filter((report) => report !== null))
      .subscribe((report) => {
        this.reportIsShared = report.shared;
        this.reportIsEmbedded = report.is_embedded;
        this.reportIsWhiteLabelEmbedded = report.is_signed;
      });

    this.reportRunDataService.reportRunState$.subscribe((state) => {
      const newExecutingState = state.reportRun && state.reportRun.loading;
      this.exportableDatasets = state.models.size > 0;
      this.executing = newExecutingState;
    });

    // Performance Observability Tracking:
    // This observable looks for the first report run state loaded on the page and checks what the initial
    // state is. If the initial state is succeeded then this report page load is of a previously run report
    // and therefore a candidate to track the page load performance. We do not want to measure page load for
    // reports that are actively being run since that will effect the page load in a way Mode does not control.
    this.reportViewerFacade.reportRunState$.pipe(first()).subscribe((state) => {
      if (state == 'succeeded') {
        // track that we want to record this page load event with a performance mark to be read at
        // the point that the report page has completely rendered
        this.observabilityService.mark(ObservabilityEvents.REPORT_SUCCEEDED);
      }
    });

    this.clientDataService.accountPreference$.pipe(filter((resource) => !!resource)).subscribe((preferenceResource) => {
      this.preference = new this.preferenceModel.model(preferenceResource);
    });

    this.runToken$ = this.store.select(SelectedReportSelectors.getReportRunToken);
    this.hasRun$ = this.runToken$.pipe(map((token) => token != null));

    this.refreshReportRun();
  }

  getRunStateIfRunTokenExists(currentParam?: { [key: string]: string }): ngRouter.StateDeclaration {
    const currentState = this.$state.current;
    let runId;
    let isRunTokenIncluded = false;
    let newState = '';
    let newStateParams: { runId?: string; [key: string]: string };

    currentParam = currentParam === undefined ? this.$state.params : currentParam;

    if (currentState.name.includes('runs')) {
      runId = this.$state.params.runId;
      isRunTokenIncluded = true;
      newState = 'reportShow.runs';
      newStateParams = { runId, ...currentParam };
    } else {
      newState = 'reportShow.noRuns';
      newStateParams = currentParam;
    }
    return {
      name: newState,
      params: newStateParams,
    };
  }

  getLinkedState(queryId, type): ngRouter.StateDeclaration {
    const partialState = this.getRunStateIfRunTokenExists({ queryId });
    let newState: ngRouter.StateDeclaration = {};
    switch (type) {
      case 'query':
        newState = {
          ...partialState,
          name: `${partialState.name}.detailsView.detailsQuery`,
        };
        break;
      case 'data':
        newState = {
          ...partialState,
          name: `${partialState.name}.detailsView.detailsDataset`,
        };
        break;
      case 'explore':
      case 'lightbox':
        const { id: ligthBoxId } = queryId;
        const [vizType, vizId] = ligthBoxId.split('_');
        const lightBoxPartialState = this.getRunStateIfRunTokenExists({ vizId });
        const stateSuffix = vizType === 'python' ? 'pythonviz.view' : 'viz';
        newState = {
          ...lightBoxPartialState,
          name: `${partialState.name}.${stateSuffix}`,
        };
        break;
    }

    return newState;
  }

  public navigate(toState): void {
    this.$state.go(toState.name, toState.params);
  }

  public navigateToNewTab(toState): void {
    const url = this.$state.href(toState.name, toState.params);
    this.$window.open(url, '_blank');
  }

  public isSharingBannerEnabled() {
    return (
      (this.reportIsShared || this.reportIsEmbedded || this.reportIsWhiteLabelEmbedded) &&
      this.preference &&
      !this.preference.hidden_banners.external_sharing
    );
  }

  public dismissSharingBanner() {}

  public handleExitFullScreen() {
    this.windowMessageService.ExitFullScreen();

    this.store.dispatch(ReportViewerActions.stopPollingForNewReportRuns());
  }

  public handleEnterFullScreen() {
    if (location.href.match('/runs/')) {
      return;
    }

    this.windowMessageService.EnterFullScreen();

    this.reportViewerFacade.pollForNewReportRuns(
      this.report._resource.account_username,
      this.report.token,
      this.report.last_successful_run_token,
      this.updateReport
    );
  }

  private updateReport: any = (newReport) => {
    this.runId = newReport.last_successful_run_token;
    setMetaContent(this.$window.document, MetaTags.ReportRunToken, this.runId);

    setMetaContent(this.$window.document, MetaTags.ReportRunEntryPath, newReport.last_successful_run_api_url);
    this.reportViewerFacade.selectReport(
      newReport.account_username,
      newReport.reportToken,
      newReport.last_successful_run_token
    );
  };

  private getReportOwner(owner) {
    this.reportOwner = owner;
    this.clientDataService.loadReportOwner(owner._resource);
  }
  private loadAccountState(owner) {
    const contextAccount = owner.user ? this.account : owner;
    this.contextAccount$.next(contextAccount);
    this.clientDataService.loadAccountContext(owner._resource);
  }

  private initializeAccounts() {
    if (this.accountId) {
      this.accountModel
        .find({
          username: this.accountId,
          useEntryPath: true,
          refresh: true,
          includes: ['preference'],
        })
        .then((account) => {
          this.account$.next(account);
          this.account = account;

          // initialize rxStore client state
          this.clientDataService.loadAccount(account._resource);

          if (this.contextAccountId === this.account.username) {
            this.loadAccountState(this.account);
          } else {
            this.accountModel
              .find({
                username: this.contextAccountId,
                useEntryPath: true,
              })
              .then((ownerAccount) => this.loadAccountState(ownerAccount));
          }

          // get report owner
          if (this.reportOwnerId === this.account.username) {
            this.reportOwner = this.account;
            this.clientDataService.loadReportOwner(this.reportOwner._resource);
          } else {
            this.accountModel
              .find({
                username: this.reportOwnerId,
                useEntryPath: true,
                includes: ['organization_features'],
              })
              .then((owner) => this.getReportOwner(owner));
          }
        });
    } else {
      this.accountModel
        .find({
          username: this.reportOwnerId,
          useEntryPath: true,
          includes: ['organization_features'],
        })
        .then((owner) => this.getReportOwner(owner));
    }
  }

  private initializeFilters(reportRun) {
    reportRun.report().then((report) => {
      this.filterFacade.loadReportFilters(report.token);
      report
        .reportFilters()
        .then(this.modeCollection.elements)
        .catch(() => [])
        .then((filters) => {
          this.filterFacade.loadReportFilterResources(
            _.map(filters, function (filterResource: any) {
              return filterResource._resource;
            })
          );
        });
    });
    return reportRun;
  }

  private initializeAccountsWithReport(report) {
    if (report && this.accountId) {
      recordEvent('report-viewer-load-legacy', 'load-space', { traceId: this.runId });
      report
        .space({ includes: 'creator' })
        .then((space) => {
          this.space = space;
          recordEvent('report-viewer-load-legacy', 'load-space-success', { traceId: this.runId, space });
        })
        .catch((err) => {
          if (!err.silent) {
            return Promise.reject(err);
          }
          recordEvent('report-viewer-load-legacy', 'load-space-failed', { traceId: this.runId, err });
        });
    }
    this.initializeAccounts();
  }

  private getPythonVisualizations(pythonObservables) {
    pythonObservables.success.subscribe((reportRun) => {
      // for now just set local python to the report run
      this.reportStateService
        .getReport()
        .then(this.getPythonNotebook)
        .then((notebook) => {
          if (notebook) {
            this.savedNotebook = notebook;
            this.exportableNotebook = true;
            this.reportStateService.setNotebook(notebook);
          }
        });
    });
    pythonObservables.failure.subscribe((e) => {
      this.reportStateService.rejectNotebook(e);
    });
  }

  public getSchedulesCollection() {
    if (!this.report.public) {
      this.report
        .reportSchedules({
          includes: {
            report_schedules: {
              report_subscriptions: {
                report_subscriptions: 'new_email_subscription_membership',
              },
            },
          },
          refresh: false,
        })
        .then((reportSchedulesCollection) => {
          this.schedulesCollection$.next(reportSchedulesCollection);
        });
    }
  }

  public getReportRunsCollection() {
    this.report.reportRuns().then((reportRunsCollection) => {
      this.reportRunsCollection = reportRunsCollection;
      this.reportRunsCollection$.next(reportRunsCollection);
    });
  }

  private getPythonNotebook() {
    if (this.report.public || this.accountId) {
      return this.report.pythonNotebook();
    }
  }

  private initializeModels(reportRun) {
    recordEvent('report-viewer-load-legacy', 'load-report-run-success', {
      traceId: this.runId,
      runResource: reportRun._resource,
    });
    this.clientDataService.loadReportRunSuccess(reportRun._resource);

    this.store.dispatch({
      type: ReportRunActionType.PollReportRun,
      token: this.runId,
    });

    this.getPythonVisualizations(this.notebookDataService.pollPython());

    reportRun
      .report()
      .then((report) => this.reportModel.buildTheme(report))
      .then((report) => {
        this.report = report;
        this.report$.next(report);
        this.run = reportRun;
        this.reportRun$.next(reportRun);
        // update state management service for report
        this.reportStateService.setReportRun(reportRun);
        this.reportStateService.setReport(report);
        this.initializeAccountsWithReport(report);
      });
    return reportRun;
  }

  private handleError(e) {
    if (e && e.status) {
      this.alert.not_found = 'true';
    }

    if (e.message.includes('Needed: token')) {
      this.errorNoToken = true;
    }

    this.initializeAccounts();
  }

  private refreshReportRun() {
    // Reset report view state
    this.reportStateService.resetState();

    recordEvent('report-viewer-load-legacy', 'load-report-run', {
      traceId: this.runId,
    });

    this.reportRunModel
      .find({
        token: this.runId,
        useEntryPath: true,
        includes: [
          'executed_by',
          'pdf_export',
          'new_pdf_export',
          'new_report_run_email',
          'new_report_run_slack_message',
          {
            python_cell_runs: {
              python_cell_runs: 'python_cell_run_results',
            },
          },
          {
            report: [
              'display_properties',
              'report_filters',
              'report_theme',
              {
                queries: {
                  queries: [
                    {
                      only: _.difference(QUERY_RESOURCE_PROPERTIES, ['raw_query']),
                    },
                    'query_tables',
                  ],
                },
              },
              ...(this.isExportDatasetsEnabled
                ? [
                    {
                      local_datasets: {
                        local_datasets: [
                          {
                            only: _.difference(QUERY_RESOURCE_PROPERTIES, ['raw_query']),
                          },
                          'query_tables',
                        ],
                      },
                    },
                  ]
                : []),
              {
                python_visualizations: {
                  python_visualizations: [
                    'python_cell',
                    {
                      python_cell_run: 'python_cell_run_results',
                    },
                  ],
                },
              },
            ],
          },
        ],
      })
      .then((reportRun) => this.initializeModels(reportRun))
      .then((reportRun) => this.initializeFilters(reportRun))
      .then((queryRuns) => {
        this.datasetService.queryRuns = _.keyBy(queryRuns, 'query_token');
      })
      .catch((e) => this.handleError(e));
  }
}
